import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Quote, X, ChevronDown, ChevronUp } from 'lucide-react';
import { extractPlainText } from '../utils/markdownRenderer';

// Constants
const PANEL_CONFIG = {
  MAX_RECENT_CONTEXTS: 5,
  PREVIEW_LENGTH: 80,
  ANIMATION_DURATION: 0.2
};

const STYLES = {
  PANEL: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg w-full max-w-lg mb-2 absolute bottom-full left-4',
  HEADER: 'flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-700',
  CONTENT: 'max-h-48 overflow-y-auto',
  CONTEXT_ITEM: 'p-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer',
  CONTEXT_PREVIEW: 'text-sm text-gray-700 dark:text-gray-300 line-clamp-2 mb-1',
  CONTEXT_META: 'text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2',
  EMPTY_STATE: 'p-3 text-center text-gray-500 dark:text-gray-400 text-sm'
};

const RecentContextPanel = ({ recentContexts, onQuoteContext, onRemoveContext, isVisible, onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Memoized recent contexts (most recent first)
  const sortedContexts = useMemo(() => {
    return [...recentContexts]
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, PANEL_CONFIG.MAX_RECENT_CONTEXTS);
  }, [recentContexts]);

  // Memoized toggle function
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Memoized quote handler
  const handleQuoteContext = useCallback((context) => {
    onQuoteContext(context);
    setIsExpanded(false); // Collapse panel after quoting
    onToggle(); // Hide the entire panel after quoting
  }, [onQuoteContext, onToggle]);

  // Memoized remove handler
  const handleRemoveContext = useCallback((contextId, e) => {
    e.stopPropagation(); // Prevent quote action
    onRemoveContext(contextId);
  }, [onRemoveContext]);

  // Don't render if not visible or no contexts
  if (!isVisible || sortedContexts.length === 0) return null;

  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: PANEL_CONFIG.ANIMATION_DURATION }}
        className={STYLES.PANEL}
      >
      {/* Header */}
      <div className={STYLES.HEADER}>
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-blue-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Recent Context ({sortedContexts.length})
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleExpanded}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? (
              <ChevronUp size={14} className="text-gray-500" />
            ) : (
              <ChevronDown size={14} className="text-gray-500" />
            )}
          </button>
          <button
            onClick={onToggle}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Hide panel"
          >
            <X size={14} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: PANEL_CONFIG.ANIMATION_DURATION }}
            className={STYLES.CONTENT}
          >
            {sortedContexts.length === 0 ? (
              <div className={STYLES.EMPTY_STATE}>
                No recent context available
              </div>
            ) : (
              sortedContexts.map((context) => (
                <div
                  key={context.id}
                  className={STYLES.CONTEXT_ITEM}
                  onClick={() => handleQuoteContext(context)}
                  title="Click to quote this context"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className={STYLES.CONTEXT_PREVIEW}>
                        {extractPlainText(context.content).length > PANEL_CONFIG.PREVIEW_LENGTH
                          ? `${extractPlainText(context.content).substring(0, PANEL_CONFIG.PREVIEW_LENGTH)}...`
                          : extractPlainText(context.content)
                        }
                      </p>
                      <div className={STYLES.CONTEXT_META}>
                        <Quote size={12} className="text-blue-500" />
                        <span>Quoted {context.quoteCount} time{context.quoteCount !== 1 ? 's' : ''}</span>
                        <span>•</span>
                        <span>{new Date(context.timestamp).toLocaleTimeString()}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleRemoveContext(context.id, e)}
                      className="ml-2 p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      title="Remove from recent context"
                    >
                      <X size={12} className="text-gray-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default RecentContextPanel;
